import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useEffect } from 'react';
import { useController, useForm } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { CARRIER_CODE_TYPES, RECORD_STATUSES, carrierCodeInputSchema } from '@app/shared';
import type { CarrierCodeDto, CarrierCodeInput } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { FormDialog } from '../../components/dialogs';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { useCreateCarrierCode, useUpdateCarrierCode } from './api';
import { useCarriers } from './lookups';
import { carrierOptions, withCurrent } from './options';

/** An allowance with an "Unlimited" checkbox: `null` in the form (and the API) means unlimited. */
function AllowanceInput({
  control,
  name,
  label,
}: {
  control: Control<CarrierCodeInput>;
  name: 'voiceMinutes' | 'textCount' | 'dataMb';
  label: string;
}) {
  const { field, fieldState } = useController({ control, name });
  const unlimited = field.value === null || field.value === undefined;
  return (
    <Box>
      <TextField
        label={label}
        disabled={unlimited}
        name={field.name}
        inputRef={field.ref}
        onBlur={field.onBlur}
        inputMode="numeric"
        value={unlimited ? '' : String(field.value)}
        onChange={(e) => {
          // An empty box is 0, never "unlimited": only the checkbox sets that.
          const text = e.target.value.replace(/[^\d]/g, '');
          field.onChange(text === '' ? 0 : Number(text));
        }}
        error={Boolean(fieldState.error)}
        helperText={fieldState.error?.message}
      />
      <FormControlLabel
        label="Unlimited"
        control={
          <Checkbox
            size="small"
            checked={unlimited}
            onChange={(e) => field.onChange(e.target.checked ? null : 0)}
            inputProps={{ 'aria-label': `${label} unlimited` }}
          />
        }
      />
    </Box>
  );
}

interface Props {
  open: boolean;
  /** Present when editing. */
  carrierCode?: CarrierCodeDto;
  onClose: () => void;
}

/**
 * "Add new carrier code" and its edit form.
 * Assumption: the reference's form was not observed, so the fields are the columns of its list.
 */
export function CarrierCodeFormDialog({ open, carrierCode, onClose }: Props) {
  const notify = useNotify();
  const carriers = useCarriers(open);
  const createCode = useCreateCarrierCode();
  const updateCode = useUpdateCarrierCode();

  const { control, handleSubmit, reset, setError } = useForm<CarrierCodeInput>({
    resolver: zodResolver(carrierCodeInputSchema),
  });

  useEffect(() => {
    if (!open) return;
    reset(
      carrierCode
        ? {
            carrierId: carrierCode.carrier.id,
            code: carrierCode.code,
            type: carrierCode.type,
            voiceMinutes: carrierCode.voiceMinutes,
            textCount: carrierCode.textCount,
            dataMb: carrierCode.dataMb,
            billingCode: carrierCode.billingCode,
            status: carrierCode.status,
          }
        : {
            code: '',
            type: 'Service Plan',
            voiceMinutes: null,
            textCount: null,
            dataMb: null,
            billingCode: '',
            status: 'Active',
          },
    );
  }, [open, carrierCode, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      if (carrierCode) {
        await updateCode.mutateAsync({ id: carrierCode.id, input: values });
        notify.success('Carrier code updated.');
      } else {
        await createCode.mutateAsync(values);
        notify.success('Carrier code created.');
      }
      onClose();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  return (
    <FormDialog
      open={open}
      title={carrierCode ? 'Edit carrier code' : 'Add new carrier code'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={createCode.isPending || updateCode.isPending}
      maxWidth="md"
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
          pt: 1,
        }}
      >
        <SelectInput
          control={control}
          name="carrierId"
          label="Carrier"
          required
          options={withCurrent(
            carrierOptions(carriers.data),
            carrierCode
              ? { value: carrierCode.carrier.id, label: carrierCode.carrier.code }
              : undefined,
          )}
        />
        <TextInput control={control} name="code" label="Carrier Code" required />
        <SelectInput
          control={control}
          name="type"
          label="Type"
          options={toOptions(CARRIER_CODE_TYPES)}
        />
        <AllowanceInput control={control} name="voiceMinutes" label="Voice" />
        <AllowanceInput control={control} name="textCount" label="Text" />
        <AllowanceInput control={control} name="dataMb" label="Data" />
        <TextInput control={control} name="billingCode" label="Billing Code" />
        <SelectInput
          control={control}
          name="status"
          label="Status"
          options={toOptions(RECORD_STATUSES)}
        />
      </Box>
    </FormDialog>
  );
}
